import React, { useState, useEffect, useCallback } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import Sidebar from '../Layouts/Sidebar';
import Topbar from '../Layouts/Topbar';
import './Analytics.css';
import Swal from 'sweetalert2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const base_url = 'https://api.homehive.co.zw';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    metrics: {
      totalRevenue: 0,
      totalUsers: 0,
      totalProperties: 0,
      totalBookings: 0,
      revenueGrowth: 0,
      userGrowth: 0,
    },
    charts: {
      revenue: [],
      userGrowth: [],
      propertyDistribution: {},
    }
  });

  const fetchAnalyticsData = useCallback(async (showLoadingState = false) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${base_url}/api/admin-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: { timeRange }
      });

      // Update data with smooth transition
      setAnalyticsData(prevData => {
        const newData = response.data;
        
        // If it's the first load, just set the data directly
        if (!prevData.metrics.totalUsers) {
          return newData;
        }

        // Otherwise, check if data has changed and update with transition class
        const hasChanged = JSON.stringify(prevData) !== JSON.stringify(newData);
        if (hasChanged) {
          // Add transition class to elements that changed
          const elements = document.querySelectorAll('.metric-value');
          elements.forEach(el => {
            el.classList.add('value-transition');
            setTimeout(() => el.classList.remove('value-transition'), 600);
          });
        }

        return newData;
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data');
      if (showLoadingState) {
        Swal.fire({
          title: 'Error',
          text: 'Failed to load analytics data',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
      }
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  }, [timeRange]);

  useEffect(() => {
    // Initial load with loading state
    fetchAnalyticsData(true);

    // Set up polling without loading state
    const pollInterval = setInterval(() => {
      fetchAnalyticsData(false);
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [fetchAnalyticsData]);

  // Chart data configurations
  const revenueData = {
    labels: analyticsData.charts?.revenue?.map(item => item.label),
    datasets: [
      {
        label: 'Revenue',
        data: analyticsData.charts?.revenue?.map(item => item.value),
        borderColor: '#1a237e',
        backgroundColor: 'rgba(26, 35, 126, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const userGrowthData = {
    labels: analyticsData.charts?.userGrowth?.map(item => item.label),
    datasets: [
      {
        label: 'New Users',
        data: analyticsData.charts?.userGrowth?.map(item => item.value),
        backgroundColor: '#4caf50',
      },
    ],
  };

  const propertyDistributionData = {
    labels: Object.keys(analyticsData?.charts?.propertyDistribution),
    datasets: [
      {
        data: Object.values(analyticsData?.charts?.propertyDistribution),
        backgroundColor: [
          '#1a237e', // Deep Blue (Primary)
          '#4caf50', // Green
          '#ff9800', // Orange
          '#f44336', // Red
          '#9c27b0', // Purple
          '#00bcd4', // Cyan
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    animation: {
      duration: 600
    }
  };

  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    fetchAnalyticsData(true);
  };

  if (loading) {
    return (
      <div className="admin-analytics">
        <Sidebar />
        <Topbar />
        <div className="analytics-content">
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-analytics">
      <Sidebar />
      <Topbar />
      
      <div className="analytics-content">
        <div className="analytics-header">
          <h1>Analytics Dashboard</h1>
          <div className="time-range-selector">
            <button
              className={`time-btn ${timeRange === 'week' ? 'active' : ''}`}
              onClick={() => handleTimeRangeChange('week')}
            >
              Week
            </button>
            <button
              className={`time-btn ${timeRange === 'month' ? 'active' : ''}`}
              onClick={() => handleTimeRangeChange('month')}
            >
              Month
            </button>
            <button
              className={`time-btn ${timeRange === 'year' ? 'active' : ''}`}
              onClick={() => handleTimeRangeChange('year')}
            >
              Year
            </button>
          </div>
        </div>

        {error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Key Metrics */}
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon revenue">
                  <i className="fas fa-dollar-sign"></i>
                </div>
                <div className="metric-info">
                  <h3>Total Revenue</h3>
                  <p className="metric-value">${analyticsData.metrics.total_payments.toLocaleString()}</p>
                  <p className={`metric-growth ${analyticsData.metrics.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                    <i className={`fas fa-arrow-${analyticsData.metrics.revenueGrowth >= 0 ? 'up' : 'down'}`}></i>
                    {Math.abs(analyticsData.metrics.revenueGrowth)}% from last {timeRange}
                  </p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon users">
                  <i className="fas fa-users"></i>
                </div>
                <div className="metric-info">
                  <h3>Total Users</h3>
                  <p className="metric-value">{analyticsData.metrics.totalUsers.toLocaleString()}</p>
                  <p className={`metric-growth ${analyticsData.metrics.userGrowth >= 0 ? 'positive' : 'negative'}`}>
                    <i className={`fas fa-arrow-${analyticsData.metrics.userGrowth >= 0 ? 'up' : 'down'}`}></i>
                    {Math.abs(analyticsData.metrics.userGrowth)}% from last {timeRange}
                  </p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon properties">
                  <i className="fas fa-home"></i>
                </div>
                <div className="metric-info">
                  <h3>Total Properties</h3>
                  <p className="metric-value">{analyticsData.metrics.totalProperties.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
              <div className="chart-card">
                <h3>Revenue Overview</h3>
                <div className="chart-container">
                  <Line data={revenueData} options={chartOptions} />
                </div>
              </div>

              <div className="chart-card">
                <h3>User Growth</h3>
                <div className="chart-container">
                  <Bar data={userGrowthData} options={chartOptions} />
                </div>
              </div>

              <div className="chart-card">
                <h3>Property Distribution</h3>
                <div className="chart-container">
                  <Pie data={propertyDistributionData} options={chartOptions} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
